import React, {useEffect, useRef} from 'react';
import {ITokenSuggestion} from '../Storefront/useOpportunityTokens';

interface ITokenSuggestionListProps {
    matches: ITokenSuggestion[];
    highlight: number;
    onHover: (index: number) => void;
    onPick: (token: string) => void;
}

// Shared presentational dropdown body for token autosuggest (used by both the plain-text field and the rich-text
// editor). Renders grouped rows (General / Questions) with a highlighted item that is kept scrolled into view.
// Rows commit on mousedown with preventDefault so clicking never blurs the field/editor behind the callout.
const TokenSuggestionList: React.FC<ITokenSuggestionListProps> = ({matches, highlight, onHover, onPick}) => {
    const highlightRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        highlightRef.current?.scrollIntoView({block: 'nearest'});
    }, [highlight]);

    return (
        <div style={{maxHeight: 240, overflowY: 'auto', overflowX: 'hidden', padding: 3}} role="listbox">
            {matches.map((s, i) => {
                // Emit a subtle section header when the group changes (General → Questions).
                const header = (i === 0 || matches[i - 1].group !== s.group)
                    ? <div key={`${s.group}-header`} style={{
                        padding: '4px 8px 2px', fontSize: 10, fontWeight: 600,
                        textTransform: 'uppercase', letterSpacing: 0.5, color: '#a19f9d'
                    }}>{s.group}</div>
                    : null;
                return (
                    <React.Fragment key={s.token}>
                        {header}
                        <div role="option"
                             ref={i === highlight ? highlightRef : undefined}
                             aria-selected={i === highlight}
                             onMouseDown={(e) => {
                                 e.preventDefault();
                                 onPick(s.token);
                             }}
                             onMouseEnter={() => onHover(i)}
                             style={{
                                 display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap',
                                 padding: '3px 8px', cursor: 'pointer', borderRadius: 3,
                                 background: i === highlight ? '#f4f8ea' : 'transparent'
                             }}>
                            <code style={{fontSize: 12, color: '#605e5c', minWidth: 0, wordBreak: 'break-all'}}>{s.token}</code>
                            <span style={{color: '#8a8886', fontSize: 11, minWidth: 0}}>{s.description}</span>
                        </div>
                    </React.Fragment>
                );
            })}
        </div>
    );
};

export default TokenSuggestionList;
